/**
 * WEB TEMASI ÖLÇEKLERİ — boşluk, köşe, gölge, boy ve yazı stilleri
 * (`docs/design/yeni-gorunum/tokens.json` → `spacing`, `radius`, `shadow`,
 * `size`, `type`). Anahtarlar tasarım adlarıdır, aynen; CSS değişkeni
 * `--ib-` öneki altında kebab yazılır (K7): `space-0_5` → `--ib-space-0-5`
 * (kebab kapısı `_` kabul etmez, kural korunur), `pageTitle` →
 * `--ib-type-page-title-*`.
 *
 * Ölçekler moddan bağımsızdır, gölge iki temalıdır.
 */
/** Boşluk, px (12 basamak). */
export const webSpace = {
    'space-0_5': 2,
    'space-1': 4,
    'space-2': 8,
    'space-3': 12,
    'space-4': 16,
    'space-5': 20,
    'space-6': 24,
    'space-8': 32,
    'space-10': 40,
    'space-12': 48,
    'space-16': 64,
    'space-20': 80,
};
/** Köşe, px (8 basamak). */
export const webRadius = {
    'radius-xs': 4,
    'radius-tag': 6,
    'radius-input': 10,
    'radius-card': 16,
    'radius-dialog': 20,
    'radius-stage': 32,
    'radius-device': 44,
    'radius-pill': 999,
};
/** Boy, px (9 değer). */
export const webSize = {
    'control-height': 44,
    'control-height-sm': 36,
    'icon-sm': 16,
    'icon-md': 20,
    'icon-lg': 24,
    'sidebar-width': 264,
    'topbar-height': 64,
    'content-max': 1200,
    'bond-stroke': 2,
};
/**
 * Avatar ölçeği (Avatar kartı, yeni görünüm Aşama 1b): 24 yığın · 32 tablo ·
 * 40 kişi satırı ve menü · 56 kişi kartı · 96 profil. Anahtarlar kitin bugünkü
 * `AvatarSize` adlarıdır (çağıranlar değişmez); eski yedi basamak en yakın
 * tasarım değerine iner (26 → 24, 36 → 32, 48 ve 52 → 56, 64 → 56, 88 → 96).
 * Mobil aynı tabloyu `../mobil-tema` kapısından okur (M0).
 */
export const webAvatarSteps = [24, 32, 40, 56, 96];
export const webAvatarSize = {
    xs: 24,
    sm: 32,
    md: 40,
    lg: 56,
    lgWide: 56,
    xl: 56,
    '2xl': 96,
};
/** Gölge, iki tema (3 kademe). Koyu temada gölge kenarlıkla desteklenir. */
export const webShadow = {
    light: {
        'shadow-1': '0 1px 2px rgba(11, 31, 46, 0.06), 0 1px 1px rgba(11, 31, 46, 0.04)',
        'shadow-2': '0 8px 24px rgba(11, 31, 46, 0.10), 0 2px 6px rgba(11, 31, 46, 0.06)',
        'shadow-3': '0 24px 56px rgba(11, 31, 46, 0.18), 0 4px 12px rgba(11, 31, 46, 0.08)',
    },
    dark: {
        'shadow-1': '0 1px 2px rgba(0, 0, 0, 0.32)',
        'shadow-2': '0 8px 24px rgba(0, 0, 0, 0.45)',
        'shadow-3': '0 24px 56px rgba(0, 0, 0, 0.60)',
    },
};
/**
 * Yazı tipi: cihazın sistem fontu (kullanıcı kararı 30.10.2026). Web ve panel
 * özel yazı tipi yüklemez; başlık, gövde, düğme, etiket ve sayı aynı yığını
 * kullanır. Hiyerarşi aileyle değil boy ve ağırlıkla kurulur (başlık 700, ara
 * başlık 600, gövde 400, etiket 500). Saat, tarih ve kod gibi hizalı rakam
 * isteyen yerler `font-variant-numeric: tabular-nums` yazar.
 *
 * Tasarım sisteminde tek değişken `--font-system`tir; boruda `--ib-font-system`
 * (K7 öneki). Üç rol (`display`, `body`, `mono`) adıyla kalır: yazı stilleri ve
 * CSS modülleri rol değişkenini (`--ib-font-<rol>`) okur, üçü de
 * `var(--ib-font-system)`a bağlanır.
 */
export const webFontStack = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
/**
 * Resim üretiminin yazı tipi (OG kartı, marka kilitleri). Satori sistem fontuna
 * erişemez, TTF ister; yığındaki Roboto depoda durur (`apps/web/public/brand/fonts`).
 * Tasarım sisteminde `--font-image`.
 */
export const webImageFontFamily = 'Roboto';
export const webFontFamilies = {
    display: webFontStack,
    body: webFontStack,
    mono: webFontStack,
};
/**
 * Yazı stilleri (23): masaüstü 11, telefon 10, mono 2. Boy ve satır yüksekliği
 * tasarım paketindendir. Kullanıcı kararları (30.10.2026, sistem fontu):
 * ağırlıkta sayfa başlığı 800 yerine 700, etiket ve tablo başlığı 600 yerine
 * 500; harf aralığı her stilde 0 (LinkedIn ve Luma gibi), tek istisna
 * doğrulama kodu (`verifyCode`, okunurluk için 0.24em).
 */
export const webTypeStyles = {
    pageTitle: {
        family: 'display',
        fontSize: 48,
        lineHeight: 52,
        fontWeight: 700,
        letterSpacing: '0',
    },
    h2: {
        family: 'display',
        fontSize: 32,
        lineHeight: 38,
        fontWeight: 700,
        letterSpacing: '0',
    },
    h3: {
        family: 'display',
        fontSize: 22,
        lineHeight: 28,
        fontWeight: 600,
        letterSpacing: '0',
    },
    cardTitle: {
        family: 'display',
        fontSize: 18,
        lineHeight: 24,
        fontWeight: 600,
        letterSpacing: '0',
    },
    body: { family: 'body', fontSize: 16, lineHeight: 24, fontWeight: 400, letterSpacing: '0' },
    bodySmall: { family: 'body', fontSize: 14, lineHeight: 20, fontWeight: 400, letterSpacing: '0' },
    label: { family: 'body', fontSize: 13, lineHeight: 16, fontWeight: 500, letterSpacing: '0' },
    tableCell: { family: 'body', fontSize: 14, lineHeight: 20, fontWeight: 400, letterSpacing: '0' },
    tableHead: {
        family: 'body',
        fontSize: 13,
        lineHeight: 16,
        fontWeight: 500,
        letterSpacing: '0',
    },
    metric: {
        family: 'display',
        fontSize: 40,
        lineHeight: 44,
        fontWeight: 700,
        letterSpacing: '0',
    },
    button: {
        family: 'body',
        fontSize: 15,
        lineHeight: 20,
        fontWeight: 600,
        letterSpacing: '0',
    },
    'pageTitle-m': {
        family: 'display',
        fontSize: 34,
        lineHeight: 38,
        fontWeight: 700,
        letterSpacing: '0',
    },
    'h2-m': {
        family: 'display',
        fontSize: 26,
        lineHeight: 32,
        fontWeight: 700,
        letterSpacing: '0',
    },
    'h3-m': {
        family: 'display',
        fontSize: 20,
        lineHeight: 26,
        fontWeight: 600,
        letterSpacing: '0',
    },
    'cardTitle-m': {
        family: 'display',
        fontSize: 17,
        lineHeight: 22,
        fontWeight: 600,
        letterSpacing: '0',
    },
    'body-m': { family: 'body', fontSize: 16, lineHeight: 24, fontWeight: 400, letterSpacing: '0' },
    'bodySmall-m': {
        family: 'body',
        fontSize: 14,
        lineHeight: 20,
        fontWeight: 400,
        letterSpacing: '0',
    },
    'label-m': {
        family: 'body',
        fontSize: 13,
        lineHeight: 16,
        fontWeight: 500,
        letterSpacing: '0',
    },
    'tableCell-m': {
        family: 'body',
        fontSize: 15,
        lineHeight: 22,
        fontWeight: 400,
        letterSpacing: '0',
    },
    'metric-m': {
        family: 'display',
        fontSize: 32,
        lineHeight: 36,
        fontWeight: 700,
        letterSpacing: '0',
    },
    'button-m': { family: 'body', fontSize: 16, lineHeight: 20, fontWeight: 600, letterSpacing: '0' },
    code: { family: 'mono', fontSize: 14, lineHeight: 20, fontWeight: 500, letterSpacing: '0' },
    verifyCode: {
        family: 'mono',
        fontSize: 28,
        lineHeight: 36,
        fontWeight: 600,
        letterSpacing: '0.24em',
    },
};
//# sourceMappingURL=olcekler.js.map